import { useCallback, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import * as PromoService from '~/services/PromoService';

export type UseOfferMetricsParams = {
  offerId?: string;
  page?: number;
  region?: string;
  limit?: number;
};

export type UseOfferMetricsResult = {
  data: Array<PromoService.PromoOfferMetric>;
  isLoading: boolean;
  isMockLoading: boolean;
  isSyncLoading: boolean;
  syncedCount: number;
  mockedCount: number;
  totalCount: number;
  fetchOfferMetrics: (params?: Partial<PromoService.FetchOfferMetricsParams>) => Promise<void>;
  addMockOfferMetricsData: (params?: Partial<PromoService.AddMockOfferMetricsDataParams>) => Promise<void>;
  syncOfferMetrics: (params?: Partial<PromoService.SyncPromoOfferMetricsParams>) => Promise<void>;
  error: string | null;
};

export function usePromoOfferMetrics(initialParams?: UseOfferMetricsParams): UseOfferMetricsResult {
  const [data, setData] = useState<Array<PromoService.PromoOfferMetric>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [syncedCount, setSyncedCount] = useState(0);
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const [mockedCount, setMockedCount] = useState(0);
  const [isMockLoading, setIsMockLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const ongoingRequestRef = useRef<Promise<void> | null>(null);

  const fetchOfferMetrics = useCallback(
    async (params: PromoService.FetchOfferMetricsParams) => {
      // Prevent duplicate requests
      if (ongoingRequestRef.current) {
        return ongoingRequestRef.current;
      }

      setIsLoading(true);
      setError(null);

      const requestPromise = (async () => {
        try {
          const response = await PromoService.getPromoOfferMetrics(
            params.offerId,
            Number(params.page),
            params.region ?? 'AU',
            params.brand ?? 'luxuryescapes',
            Number(params.limit),
            params.sortBy,
            params.sortOrder,
          );

          setData(response.result.offerMetrics || []);
          setTotalCount(response.result.total || 0);
        } catch (err) {
          const errorMessage =
            err instanceof Error ? `${err.message} - ${JSON.stringify(err)}` : 'Failed to fetch offer metrics';
          setError(errorMessage);

          enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
          setIsLoading(false);
          ongoingRequestRef.current = null;
        }
      })();

      ongoingRequestRef.current = requestPromise;
      return requestPromise;
    },
    [initialParams],
  );

  const addMockOfferMetricsData = useCallback(
    async (params: PromoService.AddMockOfferMetricsDataParams) => {
      // Prevent duplicate requests
      if (ongoingRequestRef.current) {
        return ongoingRequestRef.current;
      }

      setIsMockLoading(true);
      setError(null);

      const requestPromise = (async () => {
        try {
          const response = await PromoService.addMockOfferMetricsData({
            mockOfferMetrics: params.mockOfferMetrics,
            chunkSize: 999,
          });

          setMockedCount(response.result.mocked_count || 0);
          enqueueSnackbar('Mock offer metrics data added successfully', { variant: 'success' });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to add mock offer metrics data';
          setError(errorMessage);

          enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
          setIsMockLoading(false);
          ongoingRequestRef.current = null;
        }
      })();

      ongoingRequestRef.current = requestPromise;
      return requestPromise;
    },
    [enqueueSnackbar],
  );

  const syncOfferMetrics = useCallback(
    async (params?: Partial<PromoService.SyncPromoOfferMetricsParams>) => {
      // Prevent duplicate requests
      if (ongoingRequestRef.current) {
        return ongoingRequestRef.current;
      }

      setIsSyncLoading(true);
      setError(null);

      const requestPromise = (async () => {
        try {
          const syncParams = {
            chunkSize: params?.chunkSize || 100,
            totalMaxLimit: 10,
            batchSize: params?.chunkSize || 100,
            startAt: 1,
            region: initialParams?.region || 'AU',
            ...params,
          };

          const response = await PromoService.syncPromoOfferMetrics(syncParams);

          setSyncedCount(response.result.synced_count || 0);
          enqueueSnackbar('Offer metrics synced successfully', { variant: 'success' });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to sync offer metrics';

          enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
          setIsSyncLoading(false);
          ongoingRequestRef.current = null;
        }
      })();

      ongoingRequestRef.current = requestPromise;
      return requestPromise;
    },
    [initialParams, enqueueSnackbar],
  );

  return {
    data,
    isLoading,
    isMockLoading,
    isSyncLoading,
    syncedCount,
    mockedCount,
    totalCount,
    fetchOfferMetrics,
    addMockOfferMetricsData,
    syncOfferMetrics,
    error,
  };
}
